import { ApplicationController } from "../../support/application-controller";

function escapeHtml(str) {
    if (!str) return '';
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(String(str)));
    return div.innerHTML;
}

function parseErrorString(errorStr) {
    if (typeof errorStr !== 'string') {
        if (errorStr && typeof errorStr === 'object') return errorStr;
        return { message: String(errorStr || 'Unknown error') };
    }
    var match = errorStr.match(/HTTP status code:\s*(\d+)[\s\S]*?response body:\s*([\s\S]+)/);
    if (match) {
        var status = parseInt(match[1]);
        try {
            var body = JSON.parse(match[2]);
            return { status: status, type: body.category || body.status, message: body.message || errorStr, raw: errorStr };
        } catch(e) {}
    }
    return { message: errorStr };
}

function renderError(errorStr) {
    var parsed = parseErrorString(errorStr);
    var summary = '';
    if (parsed.status) summary += parsed.status + ' ';
    if (parsed.type) summary += parsed.type + ': ';
    summary += parsed.message || 'Unknown error';

    var html = '<span class="hubspot-tools__error-summary">' + escapeHtml(summary) + '</span>';

    if (parsed.raw && parsed.raw !== summary) {
        html += ' <button class="hubspot-tools__error-toggle">Show details</button>';
        html += '<pre class="hubspot-tools__error-details" style="display:none;">' + escapeHtml(parsed.raw) + '</pre>';
    }
    return html;
}

function statusBadge(status) {
    var cssStatus = String(status || '').replace(/_/g, '-');
    return '<span class="hubspot-tools__status-badge hubspot-tools__status-badge--' + escapeHtml(cssStatus) + '">' + escapeHtml(status) + '</span>';
}

function renderFilterChips(statusCounts) {
    var total = Object.values(statusCounts).reduce(function(a, b) { return a + b; }, 0);
    var html = '<div class="hubspot-tools__filter-chips">';
    html += '<button type="button" class="hubspot-tools__filter-chip hubspot-tools__filter-chip--active" data-filter="all">All (' + total + ')</button>';
    var order = ['created', 'would_create', 'skipped', 'error'];
    order.forEach(function(status) {
        if (statusCounts[status]) {
            html += '<button type="button" class="hubspot-tools__filter-chip" data-filter="' + escapeHtml(status) + '">' + escapeHtml(status.replace(/_/g, ' ')) + ' (' + statusCounts[status] + ')</button>';
        }
    });
    // Any remaining statuses not in the predefined order
    Object.keys(statusCounts).forEach(function(status) {
        if (order.indexOf(status) === -1) {
            html += '<button type="button" class="hubspot-tools__filter-chip" data-filter="' + escapeHtml(status) + '">' + escapeHtml(status.replace(/_/g, ' ')) + ' (' + statusCounts[status] + ')</button>';
        }
    });
    html += '</div>';
    return html;
}

function bindFilterChips(container) {
    var chips = container.querySelectorAll('.hubspot-tools__filter-chip');
    var table = container.querySelector('.hubspot-tools__filterable-table');
    if (!chips.length || !table) return;

    chips.forEach(function(chip) {
        chip.addEventListener('click', function() {
            chips.forEach(function(c) { c.classList.remove('hubspot-tools__filter-chip--active'); });
            chip.classList.add('hubspot-tools__filter-chip--active');
            var filter = chip.getAttribute('data-filter');
            var rows = table.querySelectorAll('tbody tr');
            rows.forEach(function(row) {
                if (filter === 'all' || row.getAttribute('data-status') === filter) {
                    row.style.display = '';
                } else {
                    row.style.display = 'none';
                }
            });
        });
    });
}

/**
 * Stimulus controller for HubSpot backfill operations.
 * Handles stats display, analyze/run workflow with progress polling.
 */
export default class extends ApplicationController {
    static targets = [
        "statsPanel",
        "statsContent",
        "progressPanel",
        "progressBar",
        "progressText",
        "statusMessage",
        "resultsPanel",
        "resultsContent",
        "analyzeButton",
        "runButton",
        "limitInput",
        "skipAnalyzeCheckbox",
        "skipAnalyzeWarning"
    ];

    connect() {
        this.polling = false;
        this.checkInitialStatus();
        if (this.hasResultsContentTarget) {
            this.resultsContentTarget.addEventListener('click', function(e) {
                if (e.target.classList.contains('hubspot-tools__error-toggle')) {
                    var details = e.target.nextElementSibling;
                    if (details) {
                        var isHidden = details.style.display === 'none';
                        details.style.display = isHidden ? 'block' : 'none';
                        e.target.textContent = isHidden ? 'Hide details' : 'Show details';
                    }
                }
            });
        }
    }

    disconnect() {
        this.stopPolling();
    }

    // Actions

    fetchStats(event) {
        if (event) event.preventDefault();
        this.disableButtons();
        this.showMessage("Gathering statistics...");

        fetch("/admin/hubspot/backfill/stats", {
            headers: { "Accept": "application/json", "X-CSRF-Token": this.getCSRFToken() },
            credentials: "same-origin"
        })
            .then(function(r) { return r.json(); })
            .then(function(data) {
                this.enableButtons();
                if (data.success && data.stats) {
                    this.renderStats(data.stats);
                } else {
                    this.showMessage("Failed to fetch stats: " + (data.error || "Unknown error"));
                }
            }.bind(this))
            .catch(function(err) {
                this.enableButtons();
                this.showMessage("Error: " + err.message);
            }.bind(this));
    }

    startAnalyze(event) {
        if (event) event.preventDefault();
        var limit = this.hasLimitInputTarget ? this.limitInputTarget.value : null;

        this.disableButtons();
        this.showProgress();

        var body = {};
        if (limit) body.limit = parseInt(limit, 10);

        fetch("/admin/hubspot/backfill/analyze", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "X-CSRF-Token": this.getCSRFToken()
            },
            credentials: "same-origin",
            body: JSON.stringify(body)
        })
            .then(function(r) { return r.json(); })
            .then(function(data) {
                if (data.success) {
                    this.startPolling();
                } else {
                    this.enableButtons();
                    this.showMessage("Failed: " + (data.error || "Unknown error"));
                }
            }.bind(this))
            .catch(function(err) {
                this.enableButtons();
                this.showMessage("Error: " + err.message);
            }.bind(this));
    }

    startRun(event) {
        if (event) event.preventDefault();

        var skipAnalyze = this.hasSkipAnalyzeCheckboxTarget && this.skipAnalyzeCheckboxTarget.checked;
        if (skipAnalyze && !confirm("Are you sure you want to skip the analyze step? Mappings will be created immediately.")) {
            return;
        }

        var limit = this.hasLimitInputTarget ? this.limitInputTarget.value : null;

        this.disableButtons();
        this.showProgress();

        var body = { skip_analyze: skipAnalyze };
        if (limit) body.limit = parseInt(limit, 10);

        fetch("/admin/hubspot/backfill/run", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "X-CSRF-Token": this.getCSRFToken()
            },
            credentials: "same-origin",
            body: JSON.stringify(body)
        })
            .then(function(r) { return r.json(); })
            .then(function(data) {
                if (data.success) {
                    this.startPolling();
                } else {
                    this.enableButtons();
                    this.showMessage("Failed: " + (data.error || "Unknown error"));
                }
            }.bind(this))
            .catch(function(err) {
                this.enableButtons();
                this.showMessage("Error: " + err.message);
            }.bind(this));
    }

    toggleSkipAnalyzeWarning() {
        if (this.hasSkipAnalyzeWarningTarget && this.hasSkipAnalyzeCheckboxTarget) {
            this.skipAnalyzeWarningTarget.style.display = this.skipAnalyzeCheckboxTarget.checked ? "block" : "none";
        }
    }

    loadResults(event) {
        if (event) event.preventDefault();

        fetch("/admin/hubspot/backfill/results", {
            headers: { "Accept": "application/json" },
            credentials: "same-origin"
        })
            .then(function(r) { return r.json(); })
            .then(function(data) {
                if (data.success && data.results) {
                    this.renderResults(data.results);
                }
            }.bind(this))
            .catch(function(err) {
                console.error("Failed to load results:", err);
            }.bind(this));
    }

    // Private

    checkInitialStatus() {
        fetch("/admin/hubspot/backfill/status", {
            headers: { "Accept": "application/json" },
            credentials: "same-origin"
        })
            .then(function(r) { return r.json(); })
            .then(function(data) {
                if (data.status === "running") {
                    this.disableButtons();
                    this.showProgress();
                    this.updateProgress(data);
                    this.startPolling();
                } else if (data.status === "completed" || data.status === "failed") {
                    this.showMessage(data.message || data.status);
                    this.loadResults();
                }
            }.bind(this))
            .catch(function() {});
    }

    startPolling() {
        if (this.polling) return;
        this.polling = true;
        this.pollInterval = setInterval(function() { this.poll(); }.bind(this), 3000);
    }

    stopPolling() {
        if (this.pollInterval) {
            clearInterval(this.pollInterval);
            this.pollInterval = null;
        }
        this.polling = false;
    }

    poll() {
        fetch("/admin/hubspot/backfill/status", {
            headers: { "Accept": "application/json" },
            credentials: "same-origin"
        })
            .then(function(r) { return r.json(); })
            .then(function(data) {
                this.updateProgress(data);
                if (data.status !== "running") {
                    this.stopPolling();
                    this.enableButtons();
                    this.loadResults();
                }
            }.bind(this))
            .catch(function(err) {
                console.error("Poll failed:", err);
            });
    }

    updateProgress(data) {
        if (this.hasStatusMessageTarget) {
            this.statusMessageTarget.textContent = data.message || "";
        }
        if (data.progress && this.hasProgressBarTarget && this.hasProgressTextTarget) {
            var pct = data.progress.total > 0
                ? Math.round((data.progress.processed / data.progress.total) * 100)
                : 0;
            this.progressBarTarget.style.width = pct + "%";
            this.progressTextTarget.textContent = data.progress.processed + " / " + data.progress.total;
        }
    }

    renderStats(stats) {
        if (!this.hasStatsPanelTarget || !this.hasStatsContentTarget) return;

        this.statsPanelTarget.style.display = "block";
        this.statsContentTarget.innerHTML =
            '<table class="hubspot-tools__stats-table">' +
            '<tr><td>Subscriptions with HubSpot mapping</td><td><strong>' + stats.total_with_hubspot + '</strong></td></tr>' +
            '<tr><td>Missing contact mapping</td><td><strong>' + stats.missing_contact + '</strong></td></tr>' +
            '<tr><td>Missing user deal mapping</td><td><strong>' + stats.missing_user_deal + '</strong></td></tr>' +
            '<tr><td>Missing org deal mapping</td><td><strong>' + stats.missing_org_deal + '</strong></td></tr>' +
            '<tr><td>Fully mapped</td><td><strong>' + stats.fully_mapped + '</strong></td></tr>' +
            '</table>';

        this.showMessage("Statistics loaded.");
    }

    renderResults(results) {
        if (!this.hasResultsPanelTarget || !this.hasResultsContentTarget) return;

        this.resultsPanelTarget.style.display = "block";
        if (this.hasProgressPanelTarget) this.progressPanelTarget.style.display = "none";
        var html = '<div class="hubspot-tools__results">';
        html += '<p><strong>Mode:</strong> ' + escapeHtml(results.mode || 'stats') + '</p>';

        if (results.analyzed !== undefined) {
            html += '<p>Analyzed: ' + escapeHtml(String(results.analyzed)) +
                ' | Created: ' + escapeHtml(String(results.created_count)) +
                ' | Skipped: ' + escapeHtml(String(results.skipped_count)) +
                ' | Errors: ' + escapeHtml(String(results.error_count)) + '</p>';
        }

        if (results.subscriptions && results.subscriptions.length > 0) {
            // Collect unique statuses for filter chips
            var statusCounts = {};
            results.subscriptions.forEach(function(sub) {
                var s = sub.status || 'unknown';
                statusCounts[s] = (statusCounts[s] || 0) + 1;
            });

            html += renderFilterChips(statusCounts);

            html += '<table class="hubspot-tools__results-table hubspot-tools__filterable-table">';
            html += '<thead><tr><th>Subscription</th><th>Status</th><th>Details</th></tr></thead><tbody>';
            var maxRows = Math.min(results.subscriptions.length, 100);
            for (var i = 0; i < maxRows; i++) {
                var sub = results.subscriptions[i];
                var details = "";
                if (sub.error) {
                    details = renderError(sub.error);
                } else if (sub.types) {
                    details = escapeHtml(sub.types.join(", "));
                } else if (sub.created) {
                    details = escapeHtml(sub.created.join(", "));
                } else if (sub.reason) {
                    details = escapeHtml(sub.reason);
                }
                html += '<tr data-status="' + escapeHtml(sub.status || 'unknown') + '"><td><a href="/admin/subscriptions_finder/' + encodeURIComponent(sub.subscription_id) + '">#' + escapeHtml(String(sub.subscription_id)) + '</a></td><td>' + statusBadge(sub.status) + '</td><td>' + details + '</td></tr>';
            }
            html += '</tbody></table>';
            if (results.subscriptions.length > 100) {
                html += '<p><em>Showing first 100 of ' + results.subscriptions.length + ' results.</em></p>';
            }
        }

        html += '</div>';
        this.resultsContentTarget.innerHTML = html;
        bindFilterChips(this.resultsContentTarget);
    }

    showProgress() {
        if (this.hasProgressPanelTarget) this.progressPanelTarget.style.display = "block";
        if (this.hasProgressBarTarget) this.progressBarTarget.style.width = "0%";
        if (this.hasProgressTextTarget) this.progressTextTarget.textContent = "";
    }

    showMessage(msg) {
        if (this.hasStatusMessageTarget) this.statusMessageTarget.textContent = msg;
    }

    disableButtons() {
        if (this.hasAnalyzeButtonTarget) this.analyzeButtonTarget.disabled = true;
        if (this.hasRunButtonTarget) this.runButtonTarget.disabled = true;
    }

    enableButtons() {
        if (this.hasAnalyzeButtonTarget) this.analyzeButtonTarget.disabled = false;
        if (this.hasRunButtonTarget) this.runButtonTarget.disabled = false;
    }

    getCSRFToken() {
        var meta = document.querySelector('meta[name="csrf-token"]');
        return meta ? meta.getAttribute("content") : "";
    }
}
